import { styled } from '@mui/material';
import { SettingsFieldRow, SettingsSection } from 'components/Settings';
import useEditProfileForm from 'hooks/settings/useEditProfileForm';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Form, Icon, Stack, Text, TextInput } from 'shell/components';
import { EDIT_PROFILE_FIELD } from 'utils/settings';
import { SettingsPageContext } from '../../PageProvider';
import EditProfileImageDialog from './EditProfileImageDialog';

const EditProfile = ({ sectionId }) => {
    const { setSectionDirty } = useContext(SettingsPageContext);
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const { form, errors, updateAndSave, updateField, save, saveField } = useEditProfileForm({
        onDirtyChange: (value) => setSectionDirty('edit-profile', value),
    });
    const handleProfileChange = (key, value) => updateAndSave(key, value);

    return (
        <SettingsSection id={sectionId} sectionGap={4} title={t('account-settings.profile.title', 'Edit Profile')}>
            <Form
                onSubmit={(event) => {
                    event.preventDefault();
                    save();
                }}
            >
                <Stack spacing={5}>
                    <SettingsFieldRow
                        label={<Text variant='p2'>{t('account-settings.profile.avatar', 'Profile Avatar')}</Text>}
                    >
                        <AvatarButton
                            type='button'
                            aria-label={t('account-settings.profile.edit-image', 'Edit profile image')}
                            onClick={() => setDialogOpen(true)}
                        >
                            <Badge
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={<EditIcon aria-hidden='true' kind='edit' />}
                                overlap='circular'
                            >
                                <Avatar sx={{ width: 45, height: 45 }} user={form} />
                            </Badge>
                        </AvatarButton>
                    </SettingsFieldRow>
                    <SettingsFieldRow
                        label={
                            <Text variant='p2'>
                                {t('account-settings.profile.personal-profile', 'Personal Profile')}
                            </Text>
                        }
                    >
                        <Stack spacing={3} sx={{ width: '100%' }}>
                            <TextInput
                                error={Boolean(errors.first_name)}
                                helperText={errors.first_name}
                                label={t('account-settings.profile.first-name', 'First Name')}
                                required
                                value={form.first_name || ''}
                                onChange={(event) => updateField(EDIT_PROFILE_FIELD.FIRST_NAME, event.target.value)}
                                onBlur={() => saveField(EDIT_PROFILE_FIELD.FIRST_NAME)}
                            />
                            <TextInput
                                error={Boolean(errors.last_name)}
                                helperText={errors.last_name}
                                label={t('account-settings.profile.last-name', 'Last Name')}
                                required
                                value={form.last_name || ''}
                                onChange={(event) => updateField(EDIT_PROFILE_FIELD.LAST_NAME, event.target.value)}
                                onBlur={() => saveField(EDIT_PROFILE_FIELD.LAST_NAME)}
                            />
                            <TextInput
                                disabled
                                helperText={
                                    errors.email ||
                                    t(
                                        'account-settings.profile.email-cannot-change',
                                        'Your email address cannot be changed. If you wish to use a new email, please create a new account.'
                                    )
                                }
                                label={t('account-settings.profile.email', 'Email')}
                                required
                                value={form.email || ''}
                            />
                        </Stack>
                    </SettingsFieldRow>
                </Stack>
            </Form>
            <EditProfileImageDialog
                open={dialogOpen}
                form={form}
                onClose={() => setDialogOpen(false)}
                onChange={handleProfileChange}
            />
        </SettingsSection>
    );
};

const AvatarButton = styled('button')(({ theme }) => ({
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    padding: 0,
    marginLeft: 'auto',
    '&:focus-visible': { outline: `3px solid ${theme.palette.ensightful.secondary[400]}`, outlineOffset: 3 },
}));

const EditIcon = styled(Icon)(({ theme }) => ({
    alignItems: 'center',
    background: theme.palette.ensightful.primary[500],
    border: '3px solid white',
    borderRadius: '50%',
    color: theme.palette.ensightful.shades[0],
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    width: 20,
    padding: theme.spacing(1),
    '& svg': { fontSize: 8 },
}));

export default EditProfile;
